import React, { useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { DesktopWindows, PhoneIphone } from '@mui/icons-material';

interface SamPreviewProps {
  campaignId: string;
  campaignType?: string;
}

const SamPreview: React.FC<SamPreviewProps> = ({ campaignId, campaignType }) => {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'desktop' | 'mobile' | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load preview');
  };

  const previewUrl = `/api/sam-preview/${campaignId}${campaignType ? `?type=${campaignType}` : ''}`;

  const iframeStyles = {
    desktop: {
      width: '100%',
      height: '600px',
      border: '1px solid #ddd',
      borderRadius: '4px',
    },
    mobile: {
      width: '375px',
      height: '600px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      margin: '0 auto',
      display: 'block',
    },
  };

  return (
    <Box>
      {/* View Mode Toggle */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Email Preview</Typography>
        <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
          <ToggleButton value="desktop" aria-label="desktop view">
            <DesktopWindows sx={{ mr: 1 }} />
            Desktop
          </ToggleButton>
          <ToggleButton value="mobile" aria-label="mobile view">
            <PhoneIphone sx={{ mr: 1 }} />
            Mobile
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Preview Container */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          backgroundColor: '#f5f5f5',
          position: 'relative',
          minHeight: '620px',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error ? (
          <Alert severity="error" sx={{ margin: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: viewMode === 'mobile' ? 'center' : 'stretch',
            }}
          >
            <iframe
              src={previewUrl}
              style={iframeStyles[viewMode]}
              title="SAM Preview"
              sandbox="allow-same-origin allow-scripts"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              loading="lazy"
            />
          </Box>
        )}

        {/* Device Frame for Mobile View */}
        {viewMode === 'mobile' && (
          <Box
            sx={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '395px',
              height: '640px',
              border: '20px solid #333',
              borderRadius: '30px',
              backgroundColor: '#333',
              pointerEvents: 'none',
              zIndex: 0,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-15px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '6px',
                backgroundColor: '#333',
                borderRadius: '3px',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-15px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '60px',
                backgroundColor: '#333',
                borderRadius: '50%',
              },
            }}
          />
        )}
      </Paper>

      {/* Instructions */}
      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          This preview shows how the email appears to recipients. Use the toggle buttons above to
          switch between desktop and mobile views.
        </Typography>
      </Box>
    </Box>
  );
};

export default SamPreview;
